import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { Tooltip } from "../../../ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../ui/collapsible";
import {
  Edit,
  Trash2,
  AlertTriangle,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Tag as TagIcon
} from "lucide-react";
import { cn } from "../../../../lib/utils";
import { DecisionVariable, DecisionVariablesRegistry } from "../utils/types";

interface VariablesTabProps {
  decisionVariablesRegistry: DecisionVariablesRegistry;
  onUpdateDecisionVariablesRegistry: (registry: DecisionVariablesRegistry) => void;
  nodes: any[];
  onUpdateNodes?: (nodes: any[]) => void;
  onNodeFocus?: (nodeId: string) => void;
}

const VariablesTab: React.FC<VariablesTabProps> = ({
  decisionVariablesRegistry,
  onUpdateDecisionVariablesRegistry,
  nodes,
  onUpdateNodes,
  onNodeFocus
}) => {
  
  
  const [editingVariable, setEditingVariable] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const [expandedDecisionNodes, setExpandedDecisionNodes] = useState<string[]>([]);
  const [expandedSettingNodes, setExpandedSettingNodes] = useState<string[]>([]);

  // Pre-compute variable usage maps to avoid O(V×N) performance issues
  const variableUsageMaps = useMemo(() => {
    const usageMap = new Map<string, boolean>();
    const beingSetMap = new Map<string, boolean>();
    const nodesUsingMap = new Map<string, any[]>();
    const nodesSettingMap = new Map<string, any[]>();

    // Single pass through all nodes to compute usage for all variables
    nodes.forEach(node => {
      // Check for variables used in decision conditions
      if (node.data.entityType === "DECISION" && node.data.decisionData?.conditions) {
        node.data.decisionData.conditions.forEach((cond: any) => {
          if (cond.variable) {
            usageMap.set(cond.variable, true);
            const existing = nodesUsingMap.get(cond.variable) || [];
            nodesUsingMap.set(cond.variable, [...existing, node]);
          }
        });
      }

      // Check for variables being set
      if (node.data.variableAssignments) {
        node.data.variableAssignments.forEach((assignment: any) => {
          if (assignment.variableName) {
            beingSetMap.set(assignment.variableName, true);
            const existing = nodesSettingMap.get(assignment.variableName) || [];
            nodesSettingMap.set(assignment.variableName, [...existing, node]);
          }
        });
      }
    });

    return {
      usageMap,
      beingSetMap,
      nodesUsingMap,
      nodesSettingMap
    };
  }, [nodes]);

  const handleEditVariable = (variableName: string) => {
    setEditingVariable(variableName);
    setEditingValue(variableName);
  };

  const handleDeleteVariable = (variableName: string) => {
    const newRegistry = { ...decisionVariablesRegistry };
    delete newRegistry[variableName];
    onUpdateDecisionVariablesRegistry(newRegistry);
  };

  const handleSaveEdit = () => {
    if (!editingVariable || !editingValue.trim()) {
      toast.error("Variable name cannot be empty");
      return;
    }

    // Validate variable name format
    const namePattern = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
    if (!namePattern.test(editingValue)) {
      toast.error("Variable name must start with a letter or underscore and contain only letters, numbers, and underscores");
      return;
    }

    // Check if new name already exists (unless it's the same as the current name)
    if (editingValue !== editingVariable && decisionVariablesRegistry[editingValue]) {
      toast.error("Variable name already exists");
      return;
    }

    // Update the decision variables registry
    const newRegistry = { ...decisionVariablesRegistry };
    
    // If name changed, remove old entry
    if (editingVariable !== editingValue) {
      delete newRegistry[editingVariable];
    }
    
    // Add/update with new name - preserve existing type or default to boolean
    const existingVariable = decisionVariablesRegistry[editingVariable];
    newRegistry[editingValue] = {
      name: editingValue,
      type: existingVariable?.type || "boolean"
    };
    
    // Update all node references to the variable if the name changed
    if (editingVariable !== editingValue && onUpdateNodes) {
      const updatedNodes = nodes.map(node => {
        const updatedNode = { ...node };
        let nodeUpdated = false;
        
        // Update decision nodes that use this variable in conditions
        if (node.data.entityType === "DECISION" && node.data.decisionData?.conditions && Array.isArray(node.data.decisionData.conditions)) {
          const hasVariableInConditions = node.data.decisionData.conditions.some(
            (cond: any) => cond.variable === editingVariable
          );
          
          if (hasVariableInConditions) {
            updatedNode.data = {
              ...node.data,
              decisionData: {
                ...node.data.decisionData,
                conditions: node.data.decisionData.conditions.map((cond: any) => 
                  cond.variable === editingVariable 
                    ? { ...cond, variable: editingValue }
                    : cond
                )
              }
            };
            nodeUpdated = true;
          }
        }
        
        // Update nodes that set this variable in variable assignments
        if (node.data.variableAssignments?.some((assignment: any) => assignment.variableName === editingVariable)) {
          updatedNode.data = {
            ...updatedNode.data,
            variableAssignments: node.data.variableAssignments.map((assignment: any) => 
              assignment.variableName === editingVariable 
                ? { ...assignment, variableName: editingValue }
                : assignment
            )
          };
          nodeUpdated = true;
        }
        
        return nodeUpdated ? updatedNode : node;
      });
      
      onUpdateNodes(updatedNodes);
    }
    
    onUpdateDecisionVariablesRegistry(newRegistry);
    setEditingVariable(null);
    setEditingValue("");
  };

  const handleCancelEdit = () => {
    setEditingVariable(null);
    setEditingValue("");
  };

  // Check if a variable is being used in decision nodes - now O(1) lookup
  const isVariableInUse = (variableName: string): boolean => {
    return variableUsageMaps.usageMap.get(variableName) || false;
  };

  // Check if a variable is being set by any nodes - now O(1) lookup
  const isVariableBeingSet = (variableName: string): boolean => {
    return variableUsageMaps.beingSetMap.get(variableName) || false;
  };

  // Get nodes that set a specific variable - now O(1) lookup
  const getNodesSettingVariable = (variableName: string) => {
    return variableUsageMaps.nodesSettingMap.get(variableName) || [];
  };

  // Get nodes that use a variable in decision conditions - now O(1) lookup
  const getNodesUsingVariable = (variableName: string) => {
    return variableUsageMaps.nodesUsingMap.get(variableName) || [];
  };

  const variables = Object.values(decisionVariablesRegistry);

  return (
    <div className="h-[calc(100vh-160px)] overflow-y-auto overflow-x-hidden p-6">
      {/* Existing Variables */}
      <div>
        {variables.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="space-y-3">
              <TagIcon className="w-12 h-12 text-muted-foreground mx-auto" />
              <h3 className="text-lg font-gc-med text-foreground">
                No decision variables yet
              </h3>
              <p className="text-muted-foreground text-sm max-w-md">
                Create variables in Bot nodes using the 'Set Variables' section.
              </p>
            </div>
          </div>
        ) : (
          variables.map((variable: DecisionVariable) => {
            const inUse = isVariableInUse(variable.name);
            const beingSet = isVariableBeingSet(variable.name);
            const nodesUsing = getNodesUsingVariable(variable.name);
            const nodesSetting = getNodesSettingVariable(variable.name);
            
            const totalNodes = nodesUsing.length + nodesSetting.length;
            const isExpanded = expandedDecisionNodes.includes(`decision-${variable.name}`) || 
                             expandedSettingNodes.includes(`setting-${variable.name}`);
            
            return (
              <Card key={variable.name} className="mb-4 border-border">
                <CardContent className="p-3">
                  {/* Header row with variable name and controls */}
                  <div className="flex items-center justify-between mb-3">
                    {/* Left: Variable name or edit input */}
                    <div className="flex items-center gap-2">
                      {editingVariable === variable.name ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveEdit();
                              }
                            }}
                            autoFocus
                            className="w-32 h-6 text-xs"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleSaveEdit}
                            className="h-6 w-6 p-0 text-green-600 hover:text-green-700"
                          >
                            <Check className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelEdit}
                            className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs font-gc-med">
                            {variable.name}
                          </Badge>
                          <span className="text-xs text-muted-foreground font-gc-reg">
                            {variable.type ? variable.type.charAt(0).toUpperCase() + variable.type.slice(1) : "Boolean"}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Right: Action buttons and node count dropdown */}
                    {editingVariable !== variable.name && (
                      <div className="flex items-center gap-1">
                        <Tooltip content="Edit Variable" side="left" immediate={true}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditVariable(variable.name)}
                            disabled={editingVariable !== null}
                            className="h-6 w-6 p-0"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                        </Tooltip>
                        <Tooltip content={inUse || beingSet ? "Variable is in use" : "Delete Variable"} side="left" immediate={true}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (window.confirm("Are you sure you want to delete this variable?")) {
                                handleDeleteVariable(variable.name);
                              }
                            }}
                            disabled={inUse || beingSet}
                            className="h-6 w-6 p-0 text-red-600 hover:text-red-700 disabled:text-muted-foreground"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </Tooltip>
                        
                        {/* Node count dropdown */}
                        {totalNodes > 0 && (
                          <Collapsible
                            open={isExpanded}
                            onOpenChange={(open) => {
                              const decisionKey = `decision-${variable.name}`;
                              const settingKey = `setting-${variable.name}`;
                              
                              if (open) {
                                if (nodesUsing.length > 0) {
                                  setExpandedDecisionNodes([...expandedDecisionNodes, decisionKey]);
                                }
                                if (nodesSetting.length > 0) {
                                  setExpandedSettingNodes([...expandedSettingNodes, settingKey]);
                                }
                              } else {
                                setExpandedDecisionNodes(expandedDecisionNodes.filter(k => k !== decisionKey));
                                setExpandedSettingNodes(expandedSettingNodes.filter(k => k !== settingKey));
                              }
                            }}
                          >
                            <CollapsibleTrigger className="!flex !items-center !gap-1 !justify-start !w-auto text-xs text-muted-foreground hover:text-foreground transition-colors [&>svg]:!mr-0">
                              <span className="font-gc-med">{totalNodes}</span>
                            </CollapsibleTrigger>
                          </Collapsible>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Expanded content */}
                  {totalNodes > 0 && isExpanded && (
                    <div className="border-t border-border pt-3 space-y-3">
                      {/* Used in Decision Nodes */}
                      {nodesUsing.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-3 h-3 text-maitai-green" />
                            <span className="text-xs font-gc-med text-maitai-green">
                              Used in {nodesUsing.length} Decision Node{nodesUsing.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <div className="max-h-24 overflow-auto space-y-1">
                            {nodesUsing.map((node: any, index: number) => (
                              <Tooltip key={node.id} content="Click to focus on this node" side="left" immediate={true}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => onNodeFocus?.(node.id)}
                                  className="w-full justify-start h-auto p-2 text-xs text-left bg-accent/20 hover:bg-accent/40 border border-border/50"
                                >
                                <span className="font-gc-med text-maitai-green">#{index + 1}</span>
                                  <span className="ml-2 text-foreground">
                                    {node.data?.identifier || "Unclassified"}
                                  </span>
                                  <span className="ml-2 text-muted-foreground text-[10px]">
                                    (DECISION)
                                  </span>
                                </Button>
                              </Tooltip>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Set by Nodes */}
                      {nodesSetting.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-gc-med text-foreground">
                              Set by {nodesSetting.length} Node{nodesSetting.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <div className="max-h-24 overflow-auto space-y-1">
                            {nodesSetting.map((node: any, index: number) => {
                              // Find the variable assignment for this node to show the value
                              const assignment = node.data.variableAssignments?.find((a: any) => a.variableName === variable.name);
                              return (
                                <Tooltip key={node.id} content="Click to focus on this node" side="left" immediate={true}>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onNodeFocus?.(node.id)}
                                    className="w-full justify-start h-auto p-2 text-xs text-left bg-accent/20 hover:bg-accent/40 border border-border/50"
                                  >
                                  <span className="font-gc-med text-blue-600">#{index + 1}</span>
                                  <span className="ml-2 text-foreground">
                                    {node.data?.identifier || "Unclassified"}
                                  </span>
                                  <span className="ml-2 text-muted-foreground text-[10px]">
                                    ({node.data?.entityType || "Unknown"})
                                  </span>
                                    {assignment && (
                                      <Badge
                                        variant="outline"
                                        className="ml-2 text-[10px] px-1 py-0 border-maitai-green/30 text-maitai-green bg-maitai-green/10 hover:bg-maitai-green/20"
                                      >
                                        {assignment.value?.toString() || ""}
                                      </Badge>
                                    )}
                                  </Button>
                                </Tooltip>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default VariablesTab; 